import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { ContentType } from '../../../server/models/GenericContentFields'
import {
  ContentCardsContainer,
} from './ContentCard'
import { SelectContentTypes } from './SelectContentTypes'
import { SelectFilteredSources } from './SelectFilteredSources'
import { ListContentForSource } from './components/ListContentForSource'
import { Empty } from '../Empty'
import { DateRangePicker } from './DateRangePicker'

// context
import { SavedBrandDetailsProvider } from './contexts/useSavedBrandDetails'
import { SourceContentProvider } from './contexts/useSourceContent'
import {
  useSourcesQuery,
  type SourceType
} from '../../requests/sources'

import './styles.css'

type Props = {
    inboxId: string
}

// show past 7 days of emails and each's content
export const InboxContent: React.FC<Props> = ({ inboxId }) => {
  const now = new Date()
  const sevenDaysAgo = new Date(now.setDate(now.getDate() - 7))

  const [startDate, setStartDate] = useState<Date>(sevenDaysAgo)
  const [endDate, setEndDate] = useState<Date>()
  const [
    selectedContentTypes,
    setSelectedContentTypes
  ] = useState<ContentType[]>([
    'summary',
    'shortVideo',
    'blog',
    'carousel'
  ])
  const [filteredSources, setFilteredSources] = useState<SourceType[]>([])

  const {
    data: sources = [],
    isLoading: loading,
    isError: failed
  } = useSourcesQuery(
    inboxId,
    {
      startDate,
      endDate,
      ignore: false
    }
  )

  // Update filtered emails when emails change
  useEffect(() => {
    if (sources.length > 0) {
      setFilteredSources(sources)
    }
  }, [sources])

  const handleSourceClicked = useCallback((source: SourceType) => {
    setFilteredSources([source])
  }, [])

  const visibleSources = useMemo(() => {
    const filteredSourceIds = sources.map(src => src._id)
    return sources.filter(src => filteredSourceIds.includes(src._id))
  }, [sources, filteredSources])

  if (failed) {
    return (
      <div className="inbox-content-container">
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
        <Empty>
                    Failed to fetch inbox content. Try refreshing and trying again.
        </Empty>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="inbox-content-container">
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          disabled
        />
        <Empty>
                    Brewing up the coffee...
        </Empty>
      </div>
    )
  }

  return (
    <SavedBrandDetailsProvider>
      <div className="inbox-content-container">
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />

        <div className="filters-container-row">
          <SelectContentTypes
            selectedContentTypes={selectedContentTypes}
            onSelectedContentTypes={setSelectedContentTypes}
          />

          <SelectFilteredSources
            sources={sources}
            filteredSources={filteredSources}
            setFilteredSources={setFilteredSources}
          />
        </div>

        {
          sources.length === 0 && (
            <Empty>
              No sources found in this date range.
            </Empty>
          )
        }

        <ContentCardsContainer>
          {
            visibleSources.map((source) => (
              <SourceContentProvider
                key={source._id}
                source={source}
                inboxId={inboxId}
                onSourceClick={handleSourceClicked}
                selectedContentTypes={selectedContentTypes}
              >
                <ListContentForSource />
              </SourceContentProvider>
            ))
          }
        </ContentCardsContainer>
      </div>
    </SavedBrandDetailsProvider>
  )
}
